import { ErrorBoundary } from '@sentry/react';
import { Table } from '@statsbomb/kitbag-components';
import { VideoStatus } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { unwrappedEventDataDescriptionsAtom } from '@/atoms/eventMarkers';
import { tableColumnEntitiesAtom, tableColumnsAtom } from '@/atoms/queries/tableColumns';
import { unwrappedVisibleTableColumnsAtom } from '@/atoms/table';
import { GeneralErrorBoundaryFallback } from '@/components/errorBoundary/ErrorBoundaryFallback';
import { TablePagination } from '@/components/tables/TablePagination';
import { EVENTS_TABLE_COLUMN_ENTITIES } from '@/consts/dataLocker/events';
import { useCurrentlySelectedVideoId } from '@/hooks/useCurrentlySelectedVideoId';
import { useSortTable } from '@/hooks/useSortTable';
import { useTableColumns } from '@/hooks/useTableColumns';
import { useVisDisplayMode } from '@/hooks/useVisDisplayMode';
import { PromiseAtom } from '@/types/atom';
import { NestedObject, NestedObjectPromiseAtom } from '@/types/object';
import { StatusTranslationKey } from '@/types/status';
import { DataTableColumn } from '@/types/table';
import { loadable } from '@/utils/jotai';
import {
  getDataType,
  getFormatRule,
  getHighlightedColumnIndex,
  getValidColumnKey,
  isEventAttributeColumn,
  isLowerBetter,
} from '@/utils/table';
import { getVideoIdsFromFlatEvent } from '@/utils/video';
import { EditColumnsButton } from '../button/EditColumnsButton';
import { ContentState } from '../contentState/ContentState';
import { ColumnControlsWrapper } from '../controls/ColumnControlsWrapper';
import { ExportCsvMenuItem } from '../export/ExportCsvMenuItem';
import { MoreOptionsMenu } from '../menu/MoreOptionsMenu/MoreOptionsMenu';
import { PlayVideoButton } from '../video/PlayVideoButton';
import { CellValueToDisplay } from './CellValueToDisplay';
import { TableWrapper } from './TableWrapper';

export const EventsTableBase = ({
  events = [],
  availableColumns,
  isLoading = false,
  hasError = false,
  eventsTableRef,
  currentlySelectedVideoId,
}: {
  events?: NestedObject[];
  availableColumns?: DataTableColumn[];
  isLoading?: boolean;
  hasError?: boolean;
  eventsTableRef?: React.Ref<HTMLDivElement>;
  currentlySelectedVideoId?: string | null;
}) => {
  useTableColumns(EVENTS_TABLE_COLUMN_ENTITIES);
  const { t } = useTranslation('events');
  const { orderBy, handleSort, getSortedState } = useSortTable();
  const visibleColumns = useAtomValue(unwrappedVisibleTableColumnsAtom);
  const eventDescriptions = useAtomValue(unwrappedEventDataDescriptionsAtom);

  const eventColumnSortKeys = visibleColumns.map(key => getValidColumnKey(key));

  const sortedColumnIndex = orderBy ? eventColumnSortKeys.findIndex(key => key === orderBy) : -1;
  const highlightedColumnIndex = getHighlightedColumnIndex(sortedColumnIndex, true);

  const [visDisplayMode] = useVisDisplayMode();

  const selectedRow = () => {
    const rowIndex = events.findIndex(
      event => getVideoIdsFromFlatEvent(event).videoEventId === currentlySelectedVideoId,
    );
    return rowIndex + 1;
  };

  const hasData = events.length > 0;

  return (
    <>
      {visDisplayMode === 'both' && (
        <ColumnControlsWrapper>
          <EditColumnsButton />
          <MoreOptionsMenu>
            <ExportCsvMenuItem />
          </MoreOptionsMenu>
        </ColumnControlsWrapper>
      )}

      <TableWrapper>
        <div ref={eventsTableRef} className={hasData ? 'overflow-x-auto' : 'overflow-x-hidden'}>
          <Table
            isHeadSticky
            withBorder={false}
            highlightedColumn={highlightedColumnIndex}
            selectedRows={[selectedRow()]}
          >
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell size="regular" />
                {eventColumnSortKeys.map((key, index) => {
                  const formatRule = getFormatRule(key, availableColumns);

                  return (
                    <Table.HeaderCell
                      key={key}
                      dataType={getDataType(formatRule)}
                      sortCb={() => handleSort(key, isLowerBetter(formatRule))}
                      sortState={getSortedState(key)}
                      size="regular"
                      wrapText={isEventAttributeColumn(key)}
                    >
                      <span data-testid={`column-${index}`}>{t(`keys.${key}`)}</span>
                    </Table.HeaderCell>
                  );
                })}
              </Table.Row>
            </Table.Head>
            {hasData && (
              <Table.Body>
                {events.map(event => (
                  <Table.Row key={String(event['event.eventId'])}>
                    <Table.DataCell>
                      <PlayVideoButton
                        period={Number(event['event.period'])}
                        gameId={String(event['event.gameId'])}
                        videoStatus={event['userGame.videoStatus'] as VideoStatus}
                        eventDescriptions={eventDescriptions}
                        {...getVideoIdsFromFlatEvent(event)}
                      />
                    </Table.DataCell>
                    {visibleColumns.map(tableColumnKey => {
                      const formatRule = getFormatRule(tableColumnKey, availableColumns);
                      const key = `${event['event.eventId']}-${tableColumnKey}`;
                      return (
                        <CellValueToDisplay
                          key={key}
                          columnKey={tableColumnKey}
                          value={event[tableColumnKey]}
                          formatRule={formatRule}
                          t={t}
                          testId={key}
                        />
                      );
                    })}
                  </Table.Row>
                ))}
              </Table.Body>
            )}
          </Table>
        </div>
        {!isLoading && !hasData && !hasError && <ContentState status={StatusTranslationKey.NO_DATA} />}
        {isLoading && <ContentState status={StatusTranslationKey.LOADING} />}
        {hasError && <ContentState status={StatusTranslationKey.ERROR} />}
      </TableWrapper>
    </>
  );
};

const EventsTableWithData = ({ eventsAtom }: { eventsAtom: PromiseAtom<NestedObject[]> }) => {
  const loadableEvents = useAtomValue(loadable(eventsAtom));
  const availableColumns = useAtomValue(tableColumnsAtom);

  const { state } = loadableEvents;

  const { ref, currentlySelectedVideoId } = useCurrentlySelectedVideoId();
  const defaultProps = {
    eventsTableRef: ref,
    currentlySelectedVideoId,
    availableColumns,
  };

  if (state === 'loading') return <EventsTableBase {...defaultProps} isLoading />;

  if (state === 'hasError') return <EventsTableBase {...defaultProps} hasError />;

  return <EventsTableBase {...defaultProps} events={loadableEvents.data} />;
};

export const EventsTableWithPagination = ({
  eventsAtom,
  dataLengthAtom,
}: {
  eventsAtom: NestedObjectPromiseAtom;
  dataLengthAtom: PromiseAtom<number>;
}) => (
  <ScopeProvider atoms={[tableColumnEntitiesAtom]}>
    <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
      <Suspense fallback={<EventsTableBase isLoading />}>
        <EventsTableWithData {...{ eventsAtom }} />
      </Suspense>
      <TablePagination {...{ dataLengthAtom }} />
    </ErrorBoundary>
  </ScopeProvider>
);
